/**
 * @fileoverview AppPortalDetailsPanel — right-side detail view of the
 * currently-selected AppCard. Slots: header (large icon + "Open" primary
 * button) + metadata (title h5 + muted description).
 *
 * Foundry mapping: `application-portal-dialog__details-panel` + `__panel`.
 */

import { Button, Icon, type IconName } from "@blueprintjs/core";

interface AppPortalDetailsPanelProps {
  icon: IconName;
  title: string;
  description: string;
  openHref: string;
  /** Launch handler. Absent for an app that cannot be opened yet. */
  onOpen?: () => void;
  className?: string;
}

export function AppPortalDetailsPanel({
  icon,
  title,
  description,
  openHref,
  onOpen,
  className,
}: AppPortalDetailsPanelProps) {
  return (
    <div data-testid="app-portal-details-panel" className={["bp-app-portal-details-panel", className].filter(Boolean).join(" ")}>
      <div className="bp-app-portal-details-panel-header">
        <span className="bp-app-portal-details-panel-icon" style={{ width: 44, height: 44 }}>
          <Icon icon={icon} size={28} />
        </span>
        {/*
          The launch action. The <a> keeps a real href — middle-click and "copy
          link" still work — while the handler navigates in-SPA and closes the
          dialog, which a bare anchor would not do.
        */}
        <a
          href={openHref}
          className="bp-app-portal-details-panel-link"
          onClick={(e) => {
            if (!onOpen) return;
            e.preventDefault();
            onOpen();
          }}
        >
          <Button intent="primary" minimal rightIcon="arrow-top-right" disabled={!onOpen}>
            Open
          </Button>
        </a>
      </div>
      <div className="bp-app-portal-details-panel-metadata">
        <h5 className="bp6-heading bp-app-portal-details-panel-title">{title}</h5>
        <div className="bp6-text-muted">
          <span>{description}</span>
        </div>
      </div>
    </div>
  );
}
